import { AuthManager } from "../../services/auth-manager";
import { I18nManager } from "../../services/i18n-manager";
import { ProductService } from "../../services/product-service";
import { CartManager } from "../../state/cart-manager";
import { DialogManager } from "./dialog-manager";

// Stripe types (we'll load Stripe dynamically)
interface Stripe {
  elements: (options: any) => any;
  paymentRequest: (options: any) => any;
  confirmPayment: (options: any) => Promise<any>;
  confirmCardPayment: (
    clientSecret: string,
    data?: {
      payment_method?: {
        card: any;
        billing_details?: any;
      };
      return_url?: string;
    }
  ) => Promise<{ error?: any; paymentIntent?: any }>;
}

interface StripeElement {
  mount: (selector: string) => void;
  on: (event: string, handler: (event: any) => void) => void;
  destroy: () => void;
}

export class CartDrawer {
  private drawerElement: HTMLElement;
  private cartManager: CartManager;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private authManager: AuthManager;
  private productService: ProductService | null = null;

  // DOM elements
  private cartItems: HTMLElement | null = null;
  private cartEmpty: HTMLElement | null = null;
  private cartSummary: HTMLElement | null = null;
  private cartActions: HTMLElement | null = null;
  private cartSubtotal: HTMLElement | null = null;
  private cartTotal: HTMLElement | null = null;
  private checkoutButton: HTMLButtonElement | null = null;

  // Checkout step elements
  private checkoutSteps: HTMLElement | null = null;
  private checkoutStep1: HTMLElement | null = null;
  private checkoutStep2: HTMLElement | null = null;
  private currentCheckoutStep = 1;

  // Stripe elements
  private stripe: Stripe | null = null;
  private stripeElements: any = null;
  private paymentElement: StripeElement | null = null; // Payment Element (replaces card element)
  private expressCheckoutElement: StripeElement | null = null; // Express Checkout Element (Apple Pay, Google Pay)
  private paymentIntent: any = null;
  private processPaymentBtn: HTMLButtonElement | null = null;
  private isInitializingStripe = false; // Flag to prevent multiple simultaneous initializations

  constructor(
    cartManager: CartManager,
    i18nManager: I18nManager,
    dialogManager: DialogManager,
    authManager: AuthManager,
    productService?: ProductService
  ) {
    this.drawerElement = document.getElementById("cart-drawer") as HTMLElement;
    this.cartManager = cartManager;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
    this.authManager = authManager;
    this.productService = productService || null;

    if (!this.drawerElement) {
      console.error("Cart drawer element not found!");
      return;
    }

    this.initializeElements();
    this.setupEventListeners();
  }

  private initializeElements(): void {
    // Query from drawer-body to ensure we're looking in the right place
    const drawerBody = this.drawerElement.querySelector(".drawer-body");
    const queryRoot = drawerBody || this.drawerElement;

    this.cartItems = queryRoot.querySelector("#cart-items");
    this.cartEmpty = queryRoot.querySelector("#cart-empty");
    this.cartSummary = queryRoot.querySelector("#cart-summary");
    this.cartActions = queryRoot.querySelector("#cart-actions");
    this.cartSubtotal = queryRoot.querySelector("#cart-subtotal");
    this.cartTotal = queryRoot.querySelector("#cart-total");
    this.checkoutButton = queryRoot.querySelector(
      "#cart-checkout"
    ) as HTMLButtonElement;

    // Checkout step elements
    this.checkoutSteps = queryRoot.querySelector("#cart-checkout-steps");
    this.checkoutStep1 = queryRoot.querySelector("#cart-checkout-step-1");
    this.checkoutStep2 = queryRoot.querySelector("#cart-checkout-step-2");
    this.processPaymentBtn = queryRoot.querySelector(
      "#cart-process-payment"
    ) as HTMLButtonElement;

    // Debug: Log if button is found
    if (!this.checkoutButton) {
      console.warn(
        "Cart checkout button not found! Drawer body:",
        drawerBody,
        "Drawer element:",
        this.drawerElement
      );
    }
    if (!this.checkoutSteps) {
      console.warn("Cart checkout steps container not found!");
    }
  }

  private setupEventListeners(): void {
    // Drawer open event
    this.drawerElement.addEventListener("drawer:open", () => {
      this.showCartView(); // Reset to cart view when drawer opens
      this.renderCart();
      this.updateTranslations();
      this.setupDragHandle();
    });

    // Cart changes
    this.cartManager.subscribe(() => {
      this.renderCart();
    });

    // Language changes
    this.i18nManager.onLanguageChange(() => {
      this.updateTranslations();
      this.renderCart(); // Re-render to update translations
      // Also update checkout step translations if visible
      if (
        this.checkoutSteps &&
        !this.checkoutSteps.classList.contains("hidden")
      ) {
        this.updateCheckoutTranslations();
      }
    });

    // Auth state changes - update checkout form when user logs in/out
    this.authManager.onAuthStateChanged(() => {
      // If checkout steps are visible, update the form state
      if (
        this.checkoutSteps &&
        !this.checkoutSteps.classList.contains("hidden")
      ) {
        this.updateCheckoutFormForAuthState();
      }
    });

    // Checkout step navigation
    const backToCartBtn =
      this.drawerElement.querySelector("#cart-back-to-cart");
    backToCartBtn?.addEventListener("click", () => {
      this.showCartView();
    });

    const nextToPaymentBtn = this.drawerElement.querySelector(
      "#cart-next-to-payment"
    );
    nextToPaymentBtn?.addEventListener("click", async () => {
      if (this.validateCustomerInfo()) {
        await this.goToPaymentStep();
      }
    });

    const backToCustomerBtn = this.drawerElement.querySelector(
      "#cart-back-to-customer"
    );
    backToCustomerBtn?.addEventListener("click", () => {
      this.goToCustomerStep();
    });

    // Process payment button
    this.processPaymentBtn?.addEventListener("click", () => {
      this.processPayment();
    });

    // Continue shopping buttons (close drawer properly)
    const emptyContinueBtn = this.drawerElement.querySelector(
      "#cart-empty-continue-shopping"
    );
    emptyContinueBtn?.addEventListener("click", () => {
      this.dialogManager.closeDialog();
    });

    const continueShoppingBtn = this.drawerElement.querySelector(
      "#cart-continue-shopping"
    );
    continueShoppingBtn?.addEventListener("click", () => {
      this.dialogManager.closeDialog();
    });

    // Guest/Login toggle
    const guestCheckoutBtn = this.drawerElement.querySelector(
      "#cart-guest-checkout-btn"
    );
    const loginCheckoutBtn = this.drawerElement.querySelector(
      "#cart-login-checkout-btn"
    );
    const guestForm = this.drawerElement.querySelector(
      "#cart-guest-checkout-form"
    );
    const loginForm = this.drawerElement.querySelector(
      "#cart-login-checkout-form"
    );

    guestCheckoutBtn?.addEventListener("click", () => {
      guestCheckoutBtn.classList.add("active");
      loginCheckoutBtn?.classList.remove("active");
      guestForm?.classList.remove("hidden");
      loginForm?.classList.add("hidden");
    });

    loginCheckoutBtn?.addEventListener("click", () => {
      loginCheckoutBtn.classList.add("active");
      guestCheckoutBtn?.classList.remove("active");
      loginForm?.classList.remove("hidden");
      guestForm?.classList.add("hidden");
    });

    // Open login dialog from checkout
    const openLoginDialogBtn = this.drawerElement.querySelector(
      "#cart-open-login-dialog"
    );
    openLoginDialogBtn?.addEventListener("click", () => {
      this.dialogManager.openDialog("auth-dialog");
    });

    // Setup drag handle on init
    this.setupDragHandle();

    // Unified click handler for all cart drawer interactions
    // Use event delegation to handle dynamically rendered buttons
    this.drawerElement.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Checkout button
      const checkoutBtn = target.closest("#cart-checkout");
      if (checkoutBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.showCheckoutSteps();
        return;
      }

      // Quantity controls
      const decreaseBtn = target.closest(".quantity-decrease");
      const increaseBtn = target.closest(".quantity-increase");
      const removeBtn = target.closest(".remove-item");

      if (decreaseBtn) {
        const itemId = (decreaseBtn as HTMLElement).dataset.itemId;
        if (itemId) {
          e.preventDefault();
          this.decreaseQuantity(itemId);
        }
      } else if (increaseBtn) {
        const itemId = (increaseBtn as HTMLElement).dataset.itemId;
        if (itemId) {
          e.preventDefault();
          this.increaseQuantity(itemId);
        }
      } else if (removeBtn) {
        const itemId = (removeBtn as HTMLElement).dataset.itemId;
        if (itemId) {
          e.preventDefault();
          e.stopPropagation();
          this.removeItem(itemId);
        }
      }
    });
  }

  init(): void {
    // Initialize with empty state
    this.renderCart();
  }

  private renderCart(): void {
    const cart = this.cartManager.getCart();

    if (cart.items.length === 0) {
      this.showEmptyState();
      return;
    }

    this.showCartContent();
    this.renderCartItems(cart.items);
    this.updateCartSummary(cart);
  }

  private showEmptyState(): void {
    if (this.cartEmpty) this.cartEmpty.classList.remove("hidden");
    if (this.cartSummary) this.cartSummary.classList.add("hidden");
    if (this.cartActions) this.cartActions.classList.add("hidden");
    if (this.cartItems) this.cartItems.innerHTML = "";
  }

  private showCartContent(): void {
    if (this.cartEmpty) this.cartEmpty.classList.add("hidden");
    if (this.cartSummary) this.cartSummary.classList.remove("hidden");
    if (this.cartActions) {
      this.cartActions.classList.remove("hidden");
      // Ensure checkout button is accessible
      this.checkoutButton = this.drawerElement.querySelector(
        "#cart-checkout"
      ) as HTMLButtonElement;
    }
  }

  private renderCartItems(items: any[]): void {
    if (!this.cartItems) return;

    this.cartItems.innerHTML = items
      .map(
        (item) => `
      <div class="cart-item" data-item-id="${item.id}">
        <div class="cart-item-image">
          <image-loader src="${item.image}" alt="${item.name}"></image-loader>
        </div>
        <div class="cart-item-details">
          <h4 class="cart-item-name">${item.name}</h4>
          ${this.renderItemOptions(item.options, item.productId)}
          <div class="cart-item-price">${this.i18nManager.formatCurrency(
            item.price
          )}</div>
        </div>
        <div class="cart-item-controls">
          <div class="quantity-controls">
            <button class="quantity-btn quantity-decrease" data-item-id="${
              item.id
            }" aria-label="Decrease quantity">-</button>
            <span class="quantity-display">${item.quantity}</span>
            <button class="quantity-btn quantity-increase" data-item-id="${
              item.id
            }" aria-label="Increase quantity">+</button>
          </div>
          <button class="remove-item" data-item-id="${
            item.id
          }" aria-label="Remove item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3,6 5,6 21,6"/>
              <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
            </svg>
          </button>
        </div>
      </div>
    `
      )
      .join("");
  }

  private renderItemOptions(
    options: Record<string, any>,
    productId?: string
  ): string {
    if (!options || Object.keys(options).length === 0) {
      return "";
    }

    // Try to get localized option names and values if productService is available
    if (this.productService && productId) {
      const product = this.productService
        .getProducts()
        .find((p) => p.id === productId);
      if (product && product.options) {
        return `
          <div class="cart-item-options">
            ${Object.entries(options)
              .map(([key, value]) => {
                const optionAttribute = product.options[key];
                if (optionAttribute) {
                  const optionName =
                    this.productService!.getLocalizedOptionName(
                      optionAttribute
                    );
                  const optionValueLabel =
                    this.productService!.getLocalizedOptionValueByValue(
                      optionAttribute,
                      value as string
                    );
                  return `
                    <span class="option-item">
                      <span class="option-key">${optionName}:</span>
                      <span class="option-value">${optionValueLabel}</span>
                    </span>
                  `;
                }
                // Fallback to old behavior if option not found
                return `
                  <span class="option-item">
                    <span class="option-key">${
                      this.i18nManager.t(`product.options.${key}`) || key
                    }:</span>
                    <span class="option-value">${value}</span>
                  </span>
                `;
              })
              .join("")}
          </div>
        `;
      }
    }

    // Fallback to old behavior if productService not available or product not found
    return `
      <div class="cart-item-options">
        ${Object.entries(options)
          .map(
            ([key, value]) => `
          <span class="option-item">
            <span class="option-key">${
              this.i18nManager.t(`product.options.${key}`) || key
            }:</span>
            <span class="option-value">${value}</span>
          </span>
        `
          )
          .join("")}
      </div>
    `;
  }

  private updateCartSummary(cart: any): void {
    const subtotal = cart.items.reduce(
      (sum: number, item: any) => sum + item.price * item.quantity,
      0
    );
    const shipping = subtotal > 0 ? 49 : 0; // Only show shipping if there are items
    const total = subtotal + shipping;

    if (this.cartSubtotal) {
      this.cartSubtotal.textContent = this.i18nManager.formatCurrency(subtotal);
    }

    // Update shipping display
    const shippingElement = this.drawerElement.querySelector("#cart-shipping");
    if (shippingElement) {
      if (subtotal > 0) {
        shippingElement.textContent = this.i18nManager.formatCurrency(shipping);
        shippingElement.parentElement?.classList.remove("hidden");
      } else {
        shippingElement.parentElement?.classList.add("hidden");
      }
    }

    if (this.cartTotal) {
      this.cartTotal.textContent = this.i18nManager.formatCurrency(total);
    }
  }

  private decreaseQuantity(itemId: string): void {
    const item = this.cartManager
      .getCart()
      .items.find((item) => item.id === itemId);
    if (item && item.quantity > 1) {
      this.cartManager.updateQuantity(itemId, item.quantity - 1);
    } else if (item) {
      this.cartManager.removeItem(itemId);
    }
  }

  private increaseQuantity(itemId: string): void {
    const item = this.cartManager
      .getCart()
      .items.find((item) => item.id === itemId);
    if (item) {
      this.cartManager.updateQuantity(itemId, item.quantity + 1);
    }
  }

  private removeItem(itemId: string): void {
    this.cartManager.removeItem(itemId);
    this.dialogManager.showNotification(
      this.i18nManager.t("cart.item_removed") || "Item removed from cart",
      "success"
    );
  }

  private showCheckoutSteps(): void {
    // Keep cart view visible - don't hide anything
    // Just show checkout steps below the cart content

    // Hide the checkout button after clicking
    if (this.checkoutButton) {
      this.checkoutButton.style.display = "none";
    }

    // Show checkout steps below cart content
    if (this.checkoutSteps) {
      this.checkoutSteps.classList.remove("hidden");
      this.currentCheckoutStep = 1;
      this.updateCheckoutStepDisplay();
      this.updateCheckoutTranslations(); // Update translations when showing checkout
      this.updateCheckoutFormForAuthState(); // Update form based on auth state

      // Scroll to checkout steps smoothly
      setTimeout(() => {
        this.checkoutSteps?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } else {
      console.error("Checkout steps element not found!");
    }
  }

  private showCartView(): void {
    // Show cart view (should already be visible)
    if (this.cartItems) this.cartItems.classList.remove("hidden");
    if (this.cartSummary) this.cartSummary.classList.remove("hidden");
    if (this.cartActions) this.cartActions.classList.remove("hidden");

    // Show checkout button again
    if (this.checkoutButton) {
      this.checkoutButton.style.display = "";
    }

    // Hide checkout steps
    if (this.checkoutSteps) {
      this.checkoutSteps.classList.add("hidden");
    }

    this.currentCheckoutStep = 1;

    // Clean up Stripe elements when leaving checkout
    this.cleanupStripeElements();
    this.isInitializingStripe = false;

    // Scroll back to top of drawer
    const drawerBody = this.drawerElement.querySelector(".drawer-body");
    if (drawerBody) {
      drawerBody.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  private getComputedCSSValue(variable: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();
  }

  private async initializeStripe(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.isInitializingStripe) {
      return;
    }

    // If elements already exist, don't reinitialize
    if (
      this.paymentElement &&
      this.expressCheckoutElement &&
      this.paymentIntent
    ) {
      return;
    }

    this.isInitializingStripe = true;

    try {
      // Check if Stripe is already loaded
      if ((window as any).Stripe) {
        const pk = (window as any).__STRIPE_PUBLISHABLE_KEY__ || "";
        if (!pk || pk === "pk_test_...") {
          console.error("Stripe publishable key is not configured");
          this.isInitializingStripe = false;
          this.dialogManager.showNotification(
            this.i18nManager.t("checkout.payment_error") ||
              "Payment system is not configured. Please contact support.",
            "error"
          );
          return;
        }
        this.stripe = (window as any).Stripe(pk);
        await this.setupStripeElements();
      } else {
        // Load Stripe dynamically with timeout for mobile networks
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.async = true;
        script.crossOrigin = "anonymous";

        // Add timeout handling for slow mobile networks
        const timeout = setTimeout(() => {
          console.error("Stripe script loading timeout");
          this.isInitializingStripe = false;
          this.dialogManager.showNotification(
            this.i18nManager.t("checkout.stripe_load_error") ||
              "Payment system is loading slowly. Please try again.",
            "info"
          );
        }, 10000); // 10 second timeout

        script.onload = async () => {
          clearTimeout(timeout);
          try {
            const pk = (window as any).__STRIPE_PUBLISHABLE_KEY__ || "";
            if (!pk || pk === "pk_test_...") {
              console.error("Stripe publishable key is not configured");
              this.isInitializingStripe = false;
              this.dialogManager.showNotification(
                this.i18nManager.t("checkout.payment_error") ||
                  "Payment system is not configured. Please contact support.",
                "error"
              );
              return;
            }
            this.stripe = (window as any).Stripe(pk);
            await this.setupStripeElements();
          } catch (error) {
            console.error("Failed to initialize Stripe:", error);
            this.isInitializingStripe = false;
            this.dialogManager.showNotification(
              this.i18nManager.t("checkout.stripe_init_error") ||
                "Failed to initialize payment system. Please refresh and try again.",
              "error"
            );
          }
        };

        script.onerror = () => {
          clearTimeout(timeout);
          console.error("Failed to load Stripe script");
          this.isInitializingStripe = false;
          this.dialogManager.showNotification(
            this.i18nManager.t("checkout.stripe_load_error") ||
              "Failed to load payment system. Please check your connection and try again.",
            "error"
          );
        };

        document.head.appendChild(script);
      }
    } catch (error) {
      console.error("Failed to load Stripe:", error);
      this.isInitializingStripe = false;
    }
  }

  private async setupStripeElements(): Promise<void> {
    if (!this.stripe) {
      this.isInitializingStripe = false;
      return;
    }

    // Clean up existing elements before creating new ones
    this.cleanupStripeElements();

    // Create payment intent first to get client_secret
    // Add timeout for slow mobile networks
    try {
      await Promise.race([
        this.createPaymentIntent(),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("Payment intent creation timeout")),
            15000
          );
        }),
      ]);
    } catch (error) {
      console.error("Payment intent creation failed or timed out:", error);
      this.isInitializingStripe = false;
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_intent_error") ||
          "Payment system is taking longer than expected. Please try again.",
        "info"
      );
      return;
    }

    if (!this.paymentIntent?.client_secret) {
      console.error("Failed to create payment intent or get client_secret");
      this.isInitializingStripe = false;
      return;
    }

    // Get computed CSS values (Stripe doesn't accept CSS custom properties)
    const colorPrimary = this.getComputedCSSValue("--color-primary");
    const colorBackground = this.getComputedCSSValue(
      "--color-surface-elevated"
    );
    const colorText = this.getComputedCSSValue("--color-text-primary");
    const colorDanger = this.getComputedCSSValue("--color-error");
    const fontFamily =
      this.getComputedCSSValue("--font-family-primary") ||
      this.getComputedCSSValue("--font-family") ||
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    const borderRadius =
      this.getComputedCSSValue("--glass-border-radius-sm") || "8px";

    // Create elements with client_secret
    this.stripeElements = this.stripe.elements({
      clientSecret: this.paymentIntent.client_secret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: colorPrimary || "#6366f1",
          colorBackground: colorBackground || "#ffffff",
          colorText: colorText || "#1e293b",
          colorDanger: colorDanger || "#ef4444",
          fontFamily: fontFamily,
          spacingUnit: "4px",
          borderRadius: borderRadius,
        },
      },
    });

    // Create Express Checkout Element (for wallets: Apple Pay, Google Pay, etc.)
    // This should be mounted BEFORE the Payment Element for best UX
    // Note: applepay and googlepay don't need to be in payment_method_types
    // Express Checkout Element handles them automatically
    this.expressCheckoutElement = this.stripeElements.create(
      "expressCheckout",
      {
        buttonType: {
          applePay: "buy", // Valid values: "add-money", "book", "buy", "check-out", "contribute", "donate", "order", etc.
          googlePay: "buy",
        },
      }
    );

    // Create Payment Element (for all payment methods: cards, Klarna, MobilePay, etc.)
    // The Payment Intent explicitly specifies payment_method_types (card, klarna, mobilepay)
    // The Payment Element will automatically show all payment methods specified in the Payment Intent
    this.paymentElement = this.stripeElements.create("payment", {
      layout: "accordion",
      // Note: We don't need to set paymentMethodTypes here because the Payment Intent
      // already has them explicitly defined (card, klarna, mobilepay)
      // The Payment Element will automatically display all payment methods from the Payment Intent
    });

    // Mount Express Checkout Element first (Apple Pay, Google Pay, etc.)
    const expressCheckoutContainer = document.getElementById(
      "cart-stripe-express-checkout-element"
    );
    if (expressCheckoutContainer && this.expressCheckoutElement) {
      expressCheckoutContainer.innerHTML = "";
      this.expressCheckoutElement.mount(
        "#cart-stripe-express-checkout-element"
      );

      // Listen to ready event to see which payment methods are available
      this.expressCheckoutElement.on("ready", (event: any) => {
        // Express Checkout is ready
        // Payment methods are automatically displayed based on availability
      });

      // Handle express checkout confirmation
      this.expressCheckoutElement.on("confirm", async (event: any) => {
        // Express checkout payments are confirmed automatically
        // We just need to handle the success
        if (event.detail && event.detail.paymentIntent) {
          await this.handlePaymentSuccess(event.detail.paymentIntent);
        }
      });
    }

    // Mount Payment Element (cards, Klarna, MobilePay, etc.)
    const paymentElementContainer = document.getElementById(
      "cart-stripe-payment-element"
    );
    if (!paymentElementContainer) {
      console.error(
        "Payment element container not found: #cart-stripe-payment-element"
      );
      this.isInitializingStripe = false;
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_error") ||
          "Payment form container not found. Please refresh the page.",
        "error"
      );
      return;
    }

    if (!this.paymentElement) {
      console.error("Payment element not created");
      this.isInitializingStripe = false;
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_error") ||
          "Failed to create payment form. Please refresh the page.",
        "error"
      );
      return;
    }

    try {
      paymentElementContainer.innerHTML = "";
      this.paymentElement.mount("#cart-stripe-payment-element");

      // Listen for payment element changes to enable/disable submit button
      this.paymentElement.on("ready", () => {
        console.log("Stripe Payment Element is ready");
        const processPaymentBtn = this.drawerElement.querySelector(
          "#cart-process-payment"
        ) as HTMLButtonElement;
        if (processPaymentBtn) {
          processPaymentBtn.disabled = false;
        }
      });

      this.paymentElement.on("change", (event: any) => {
        const processPaymentBtn = this.drawerElement.querySelector(
          "#cart-process-payment"
        ) as HTMLButtonElement;
        if (processPaymentBtn) {
          // Enable button when payment method is ready
          processPaymentBtn.disabled = event.complete ? false : true;
        }
      });

      this.paymentElement.on("loaderror", (event: any) => {
        console.error("Stripe Payment Element load error:", event);
        this.dialogManager.showNotification(
          this.i18nManager.t("checkout.payment_error") ||
            "Failed to load payment form. Please refresh the page.",
          "error"
        );
      });
    } catch (error) {
      console.error("Failed to mount Stripe Payment Element:", error);
      this.isInitializingStripe = false;
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_error") ||
          "Failed to initialize payment form. Please refresh the page.",
        "error"
      );
    }

    // Mark initialization as complete
    this.isInitializingStripe = false;
  }

  private cleanupStripeElements(): void {
    // Destroy existing elements if they exist
    if (this.expressCheckoutElement) {
      try {
        this.expressCheckoutElement.destroy();
      } catch (error) {
        console.warn("Error destroying express checkout element:", error);
      }
      this.expressCheckoutElement = null;
    }

    if (this.paymentElement) {
      try {
        this.paymentElement.destroy();
      } catch (error) {
        console.warn("Error destroying payment element:", error);
      }
      this.paymentElement = null;
    }

    // Clear containers
    const expressCheckoutContainer = document.getElementById(
      "cart-stripe-express-checkout-element"
    );
    if (expressCheckoutContainer) {
      expressCheckoutContainer.innerHTML = "";
    }

    const paymentElementContainer = document.getElementById(
      "cart-stripe-payment-element"
    );
    if (paymentElementContainer) {
      paymentElementContainer.innerHTML = "";
    }

    // Reset stripeElements and paymentIntent
    this.stripeElements = null;
    this.paymentIntent = null;
  }

  private async goToPaymentStep(): Promise<void> {
    this.currentCheckoutStep = 2;
    this.updateCheckoutStepDisplay();
    // Initialize Stripe Elements when entering payment step
    await this.initializeStripe();
  }

  private goToCustomerStep(): void {
    this.currentCheckoutStep = 1;
    this.updateCheckoutStepDisplay();
    // Don't clean up Stripe elements when going back to customer step
    // They'll be reused when going back to payment step
  }

  private updateCheckoutStepDisplay(): void {
    // Update step indicators
    const stepIndicators = this.drawerElement.querySelectorAll(
      ".step-indicator-item"
    );
    stepIndicators.forEach((step, index) => {
      const stepNumber = index + 1;
      step.classList.toggle("active", stepNumber === this.currentCheckoutStep);
      step.classList.toggle("completed", stepNumber < this.currentCheckoutStep);
    });

    // Update step content
    if (this.checkoutStep1) {
      this.checkoutStep1.classList.toggle(
        "active",
        this.currentCheckoutStep === 1
      );
    }
    if (this.checkoutStep2) {
      this.checkoutStep2.classList.toggle(
        "active",
        this.currentCheckoutStep === 2
      );
    }
  }

  private validateCustomerInfo(): boolean {
    const guestForm = this.drawerElement.querySelector(
      "#cart-guest-checkout-form"
    ) as HTMLFormElement;
    const loginForm = this.drawerElement.querySelector(
      "#cart-login-checkout-form"
    );

    // If user is logged in, they don't need to fill guest form
    if (this.authManager.isAuthenticated()) {
      return true;
    }

    // If login form is visible but user is not logged in, show error
    if (loginForm && !loginForm.classList.contains("hidden")) {
      // User tried to checkout while logged out - prompt them to log in
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.login_required") ||
          "Please log in to continue with checkout",
        "error"
      );
      return false;
    }

    // Guest mode - validate form
    if (guestForm && !guestForm.checkValidity()) {
      guestForm.reportValidity();
      return false;
    }

    return true;
  }

  private updateTranslations(): void {
    // Update drawer title
    const title = this.drawerElement.querySelector("#cart-drawer-title");
    if (title) {
      title.textContent = this.i18nManager.t("cart.title") || "Shopping Cart";
    }

    // Update empty state
    const emptyTitle = this.cartEmpty?.querySelector("h3");
    const emptyDescription = this.cartEmpty?.querySelector("p");
    const continueButton = this.cartEmpty?.querySelector("button");

    if (emptyTitle) {
      emptyTitle.textContent =
        this.i18nManager.t("cart.empty") || "Your cart is empty";
    }
    if (emptyDescription) {
      emptyDescription.textContent =
        this.i18nManager.t("cart.empty_description") ||
        "Add some products to get started";
    }
    if (continueButton) {
      continueButton.textContent =
        this.i18nManager.t("cart.continue_shopping") || "Continue Shopping";
    }

    // Update summary labels
    const subtotalLabel = this.cartSummary?.querySelector(
      ".summary-row:first-child span:first-child"
    );
    const shippingLabel = this.cartSummary?.querySelector(
      ".summary-row:nth-child(2) span:first-child"
    );
    const totalLabel = this.cartSummary?.querySelector(
      ".summary-total span:first-child"
    );

    if (subtotalLabel) {
      subtotalLabel.textContent =
        this.i18nManager.t("cart.subtotal") || "Subtotal:";
    }
    if (shippingLabel) {
      shippingLabel.textContent =
        this.i18nManager.t("cart.shipping") || "Shipping:";
    }
    if (totalLabel) {
      totalLabel.textContent = this.i18nManager.t("cart.total") || "Total:";
    }

    // Update action buttons
    if (this.checkoutButton) {
      this.checkoutButton.textContent =
        this.i18nManager.t("cart.checkout") || "Proceed to Checkout";
    }

    const continueShoppingButton = this.cartActions?.querySelector(
      ".glass-button--secondary"
    );
    if (continueShoppingButton) {
      continueShoppingButton.textContent =
        this.i18nManager.t("cart.continue_shopping") || "Continue Shopping";
    }

    // Update checkout steps translations
    this.updateCheckoutTranslations();
  }

  private updateCheckoutTranslations(): void {
    // Step indicators
    const step1Label = this.drawerElement.querySelector(
      ".step-indicator-item[data-step='1'] .step-label"
    );
    const step2Label = this.drawerElement.querySelector(
      ".step-indicator-item[data-step='2'] .step-label"
    );

    if (step1Label) {
      step1Label.textContent =
        this.i18nManager.t("checkout.step.customer_info") || "Customer Info";
    }
    if (step2Label) {
      step2Label.textContent =
        this.i18nManager.t("checkout.step.payment") || "Payment";
    }

    // Step 1: Customer Information
    const step1Title = this.checkoutStep1?.querySelector("h3");
    if (step1Title) {
      step1Title.textContent =
        this.i18nManager.t("checkout.step.customer_info") ||
        "Customer Information";
    }

    const guestCheckoutBtn = this.drawerElement.querySelector(
      "#cart-guest-checkout-btn"
    );
    const loginCheckoutBtn = this.drawerElement.querySelector(
      "#cart-login-checkout-btn"
    );
    if (guestCheckoutBtn) {
      guestCheckoutBtn.textContent =
        this.i18nManager.t("checkout.guest_checkout") || "Guest Checkout";
    }
    if (loginCheckoutBtn) {
      loginCheckoutBtn.textContent =
        this.i18nManager.t("checkout.login") || "Login";
    }

    // Form labels
    const emailLabel = this.drawerElement.querySelector(
      'label[for="cart-guest-email"]'
    );
    const nameLabel = this.drawerElement.querySelector(
      'label[for="cart-guest-name"]'
    );
    const addressLabel = this.drawerElement.querySelector(
      'label[for="cart-shipping-address"]'
    );
    const cityLabel = this.drawerElement.querySelector(
      'label[for="cart-shipping-city"]'
    );
    const postalLabel = this.drawerElement.querySelector(
      'label[for="cart-shipping-postal"]'
    );
    const countryLabel = this.drawerElement.querySelector(
      'label[for="cart-shipping-country"]'
    );

    if (emailLabel) {
      emailLabel.textContent =
        this.i18nManager.t("checkout.email") + " *" || "Email Address *";
    }
    if (nameLabel) {
      nameLabel.textContent =
        this.i18nManager.t("checkout.name") + " *" || "Full Name *";
    }
    if (addressLabel) {
      addressLabel.textContent =
        this.i18nManager.t("checkout.address") + " *" || "Address *";
    }
    if (cityLabel) {
      cityLabel.textContent =
        this.i18nManager.t("checkout.city") + " *" || "City *";
    }
    if (postalLabel) {
      postalLabel.textContent =
        this.i18nManager.t("checkout.postal") + " *" || "Postal Code *";
    }
    if (countryLabel) {
      countryLabel.textContent =
        this.i18nManager.t("checkout.country") + " *" || "Country *";
    }

    // Shipping Address heading
    const shippingHeading = this.checkoutStep1?.querySelector("h4");
    if (shippingHeading && shippingHeading.textContent?.includes("Shipping")) {
      shippingHeading.textContent =
        this.i18nManager.t("checkout.shipping_address") || "Shipping Address";
    }

    // Login form
    const loginPrompt = this.drawerElement.querySelector(
      "#cart-login-checkout-form p"
    );
    const openLoginBtn = this.drawerElement.querySelector(
      "#cart-open-login-dialog"
    );
    if (loginPrompt) {
      loginPrompt.textContent =
        this.i18nManager.t("checkout.login_prompt") ||
        "Please log in to continue with your checkout.";
    }
    if (openLoginBtn) {
      openLoginBtn.textContent =
        this.i18nManager.t("checkout.login") || "Login";
    }

    // Step action buttons
    const backToCartBtn =
      this.drawerElement.querySelector("#cart-back-to-cart");
    const nextToPaymentBtn = this.drawerElement.querySelector(
      "#cart-next-to-payment"
    );
    const backToCustomerBtn = this.drawerElement.querySelector(
      "#cart-back-to-customer"
    );
    const processPaymentBtn = this.drawerElement.querySelector(
      "#cart-process-payment"
    );

    if (backToCartBtn) {
      backToCartBtn.textContent =
        this.i18nManager.t("checkout.back") || "Back to Cart";
    }
    if (nextToPaymentBtn) {
      nextToPaymentBtn.textContent =
        this.i18nManager.t("checkout.continue_to_payment") ||
        "Continue to Payment";
    }
    if (backToCustomerBtn) {
      backToCustomerBtn.textContent =
        this.i18nManager.t("checkout.back") || "Back";
    }
    if (processPaymentBtn) {
      processPaymentBtn.textContent =
        this.i18nManager.t("checkout.complete_order") || "Complete Order";
    }

    // Step 2: Payment
    const step2Title = this.checkoutStep2?.querySelector("h3");
    if (step2Title) {
      step2Title.textContent =
        this.i18nManager.t("checkout.payment_method") || "Payment Information";
    }
  }

  private async updateCheckoutFormForAuthState(): Promise<void> {
    const guestCheckoutBtn = this.drawerElement.querySelector(
      "#cart-guest-checkout-btn"
    );
    const loginCheckoutBtn = this.drawerElement.querySelector(
      "#cart-login-checkout-btn"
    );
    const authToggle = this.drawerElement.querySelector(".auth-toggle");
    const guestForm = this.drawerElement.querySelector(
      "#cart-guest-checkout-form"
    );
    const loginForm = this.drawerElement.querySelector(
      "#cart-login-checkout-form"
    );

    const isAuthenticated = this.authManager.isAuthenticated();

    if (isAuthenticated) {
      // User is logged in - hide toggle, show guest form pre-filled
      if (authToggle) {
        (authToggle as HTMLElement).style.display = "none";
      }
      guestForm?.classList.remove("hidden");
      loginForm?.classList.add("hidden");

      // Pre-fill user data
      await this.prefillUserData();
    } else {
      // User is not logged in - show toggle and guest form by default
      if (authToggle) {
        (authToggle as HTMLElement).style.display = "";
      }
      guestCheckoutBtn?.classList.add("active");
      loginCheckoutBtn?.classList.remove("active");
      guestForm?.classList.remove("hidden");
      loginForm?.classList.add("hidden");
    }
  }

  private async prefillUserData(): Promise<void> {
    if (!this.authManager.isAuthenticated()) {
      return;
    }

    const user = this.authManager.getCurrentUser();
    if (!user) {
      return;
    }

    // Try to fetch customer data from backend
    let customerData: any = null;
    try {
      const token = await this.authManager.getIdToken();
      if (token) {
        const response = await fetch("/api/account", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          customerData = await response.json();
        }
      }
    } catch (error) {
      console.error("Failed to load customer data:", error);
    }

    // Pre-fill guest form fields with user data (even if hidden)
    // This ensures the data is available when submitting
    const emailInput = this.drawerElement.querySelector(
      "#cart-guest-email"
    ) as HTMLInputElement;
    const nameInput = this.drawerElement.querySelector(
      "#cart-guest-name"
    ) as HTMLInputElement;
    const addressInput = this.drawerElement.querySelector(
      "#cart-shipping-address"
    ) as HTMLInputElement;
    const cityInput = this.drawerElement.querySelector(
      "#cart-shipping-city"
    ) as HTMLInputElement;
    const postalInput = this.drawerElement.querySelector(
      "#cart-shipping-postal"
    ) as HTMLInputElement;
    const countryInput = this.drawerElement.querySelector(
      "#cart-shipping-country"
    ) as HTMLInputElement;

    if (emailInput) {
      emailInput.value = customerData?.email || user.email || "";
    }
    if (nameInput) {
      nameInput.value = customerData?.name || user.displayName || "";
    }
    if (addressInput && customerData?.address) {
      addressInput.value =
        customerData.address.line1 || customerData.address || "";
    }
    if (cityInput && customerData?.address) {
      cityInput.value = customerData.address.city || customerData.city || "";
    }
    if (postalInput && customerData?.address) {
      postalInput.value =
        customerData.address.postal_code || customerData.postal_code || "";
    }
    if (countryInput && customerData?.address) {
      countryInput.value =
        customerData.address.country || customerData.country || "";
    }
  }

  private async createPaymentIntent(): Promise<void> {
    try {
      const cart = this.cartManager.getCart();
      const customerInfo = this.getCustomerInfo();

      // Stripe requires minimum 3.00 SEK
      // Cart total is in SEK, Stripe expects öre (smallest currency unit)
      // So we need to convert: 3 SEK = 300 öre
      const MINIMUM_SEK = 3;
      if (cart.total < MINIMUM_SEK) {
        const errorMsg =
          this.i18nManager.t("checkout.minimum_amount") ||
          `Minimum order amount is ${this.i18nManager.formatCurrency(
            MINIMUM_SEK * 100
          )}`;
        this.dialogManager.showNotification(errorMsg, "error");
        throw new Error(
          `Cart total (${cart.total} SEK) is below minimum amount (${MINIMUM_SEK} SEK)`
        );
      }

      // Convert SEK to öre for Stripe (multiply by 100)
      const amountInOre = Math.round(cart.total * 100);

      const response = await fetch("/api/checkout/create-intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: amountInOre,
          currency: "SEK",
          customer_email: customerInfo.email,
          items: cart.items.map((item) => ({
            product_id: item.productId,
            quantity: item.quantity,
            price: item.price,
            options: item.options,
          })),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Payment intent creation failed:",
          response.status,
          errorText
        );
        throw new Error(
          `Failed to create payment intent: ${response.status} ${errorText}`
        );
      }

      const data = await response.json();
      this.paymentIntent = data;
    } catch (error) {
      console.error("Failed to create payment intent:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_error") ||
          `Failed to initialize payment: ${errorMessage}`,
        "error"
      );
    }
  }

  private getCustomerInfo(): { email: string; name: string; address: any } {
    // If user is logged in, use their data but still get shipping address from form
    if (this.authManager.isAuthenticated()) {
      const user = this.authManager.getCurrentUser();
      // Get name from form field (it's populated with customer data or displayName)
      const nameInput = this.drawerElement.querySelector(
        "#cart-guest-name"
      ) as HTMLInputElement;
      // Get shipping address from the form (even logged-in users fill this out)
      const addressInput = this.drawerElement.querySelector(
        "#cart-shipping-address"
      ) as HTMLInputElement;
      const cityInput = this.drawerElement.querySelector(
        "#cart-shipping-city"
      ) as HTMLInputElement;
      const postalInput = this.drawerElement.querySelector(
        "#cart-shipping-postal"
      ) as HTMLInputElement;
      const countryInput = this.drawerElement.querySelector(
        "#cart-shipping-country"
      ) as HTMLSelectElement;

      return {
        email: user?.email || "",
        name: nameInput?.value || user?.displayName || "",
        address: {
          line1: addressInput?.value || "",
          city: cityInput?.value || "",
          postal_code: postalInput?.value || "",
          country: countryInput?.value || "",
        },
      };
    }

    // Guest user - get from form
    const guestForm = this.drawerElement.querySelector(
      "#cart-guest-checkout-form"
    ) as HTMLFormElement;
    if (!guestForm) {
      return { email: "", name: "", address: {} };
    }

    const formData = new FormData(guestForm);
    return {
      email: (formData.get("email") as string) || "",
      name: (formData.get("name") as string) || "",
      address: {
        line1: (formData.get("address") as string) || "",
        city: (formData.get("city") as string) || "",
        postal_code: (formData.get("postal") as string) || "",
        country: (formData.get("country") as string) || "",
      },
    };
  }

  private async processPayment(): Promise<void> {
    if (
      !this.stripe ||
      !this.paymentElement ||
      !this.paymentIntent ||
      !this.stripeElements
    ) {
      this.dialogManager.showNotification(
        this.i18nManager.t("checkout.payment_not_ready") ||
          "Payment system not ready. Please try again.",
        "error"
      );
      return;
    }

    if (!this.processPaymentBtn) return;

    this.processPaymentBtn.disabled = true;
    this.processPaymentBtn.textContent =
      this.i18nManager.t("checkout.processing") || "Processing...";

    try {
      if (!this.paymentIntent?.client_secret) {
        console.error(
          "Payment intent missing client_secret:",
          this.paymentIntent
        );
        throw new Error("Payment intent not initialized");
      }

      // Use confirmPayment for Payment Element (works with all payment methods: cards, Klarna, MobilePay, etc.)
      const { error, paymentIntent: confirmedPayment } =
        await this.stripe.confirmPayment({
          elements: this.stripeElements,
          confirmParams: {
            payment_method_data: {
              billing_details: this.getCustomerInfo(),
            },
          },
          redirect: "if_required",
        });

      if (error) {
        console.error("Stripe payment error:", error);
        this.dialogManager.showNotification(error.message, "error");
        this.processPaymentBtn.disabled = false;
        this.processPaymentBtn.textContent =
          this.i18nManager.t("checkout.complete_order") || "Complete Order";
      } else {
        // Check payment intent status
        const status =
          confirmedPayment?.status || confirmedPayment?.paymentIntent?.status;

        if (status === "succeeded" || status === "processing") {
          // Payment is successful or processing - create order
          await this.handlePaymentSuccess(confirmedPayment);
        } else {
          console.error("Unexpected payment status:", status);
          this.dialogManager.showNotification(
            `Payment status: ${status}. Please contact support.`,
            "error"
          );
          this.processPaymentBtn.disabled = false;
          this.processPaymentBtn.textContent =
            this.i18nManager.t("checkout.complete_order") || "Complete Order";
        }
      }
    } catch (error: any) {
      console.error("Payment processing exception:", error);
      this.dialogManager.showNotification(
        error?.message ||
          this.i18nManager.t("checkout.payment_failed") ||
          "Payment failed. Please try again.",
        "error"
      );
      this.processPaymentBtn.disabled = false;
      this.processPaymentBtn.textContent =
        this.i18nManager.t("checkout.complete_order") || "Complete Order";
    }
  }

  private async handlePaymentSuccess(paymentIntent?: any): Promise<void> {
    try {
      const cart = this.cartManager.getCart();
      const customerInfo = this.getCustomerInfo();

      // Determine payment intent ID
      const paymentIntentId =
        paymentIntent?.id ||
        paymentIntent?.paymentIntent?.id ||
        this.paymentIntent?.id;

      if (!paymentIntentId) {
        throw new Error("Payment intent ID not found");
      }

      // Get auth token if logged in
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (this.authManager.isAuthenticated()) {
        const token = await this.authManager.getIdToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      const requestData = {
        payment_intent_id: paymentIntentId,
        items: cart.items.map((item) => ({
          product_id: item.productId,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          price_sek: item.price,
          options: item.options || {},
        })),
        total_sek: cart.total,
        shipping_address: customerInfo.address,
        customer_email: customerInfo.email,
        customer_name: customerInfo.name,
        language: this.i18nManager.getCurrentLanguage(), // Send current language for email
      };

      // Create order
      const response = await fetch("/api/orders", {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(
          `Failed to create order: ${response.status} ${responseText}`
        );
      }

      const order = JSON.parse(responseText);

      // Clear cart
      this.cartManager.clearCart();

      // Close drawer
      this.dialogManager.closeDialog();

      // Open order confirmation modal with order data
      // Ensure order items have names from cart items (API may not return localized names)
      const orderData = {
        id: order.id,
        customer_email: order.customer_email || customerInfo.email,
        customer_name: order.customer_name || customerInfo.name,
        items: order.items.map((orderItem: any) => {
          // Find the corresponding cart item to get the product name
          const cartItem = cart.items.find(
            (item) => item.productId === orderItem.product_id
          );
          return {
            product_id: orderItem.product_id,
            name: cartItem?.name || orderItem.name || "Product",
            quantity: orderItem.quantity,
            price_sek: orderItem.price_sek || orderItem.price,
            options: orderItem.options || {},
          };
        }),
        subtotal_sek: order.subtotal_sek,
        shipping_sek: order.shipping_sek,
        total_sek: order.total_sek,
        shipping_address: order.shipping_address || customerInfo.address,
      };

      // Small delay to ensure drawer closes first
      setTimeout(() => {
        this.dialogManager.openDialog("order-confirmation-dialog", orderData);
      }, 300);
    } catch (error: any) {
      console.error("Failed to create order:", error);
      this.dialogManager.showNotification(
        error?.message ||
          this.i18nManager.t("checkout.order_failed") ||
          "Order creation failed. Please contact support.",
        "error"
      );
      if (this.processPaymentBtn) {
        this.processPaymentBtn.disabled = false;
        this.processPaymentBtn.textContent =
          this.i18nManager.t("checkout.complete_order") || "Complete Order";
      }
    }
  }

  private setupDragHandle(): void {
    // Add touch/mouse drag functionality
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    const drawer = this.drawerElement;

    const handleStart = (clientY: number) => {
      startY = clientY;
      currentY = clientY;
      isDragging = true;
      drawer.style.transition = "none";
    };

    const handleMove = (clientY: number) => {
      if (!isDragging) return;
      currentY = clientY;
      const deltaY = currentY - startY;

      // Only allow dragging down
      if (deltaY > 0) {
        drawer.style.transform = `translateX(-50%) translateY(${deltaY}px)`;
      }
    };

    const handleEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      drawer.style.transition = "";

      const deltaY = currentY - startY;
      const threshold = 100; // Minimum drag distance to close

      if (deltaY > threshold) {
        // Close drawer
        this.dialogManager.closeDialog();
      } else {
        // Snap back
        drawer.style.transform = "translateX(-50%) translateY(0)";
      }
    };

    // Touch events
    // Use a single non-passive listener that checks if we should handle the event
    // This reduces warnings while still allowing preventDefault when needed
    drawer.addEventListener(
      "touchstart",
      (e) => {
        // Only allow drag from top area (handle region)
        const touch = e.touches[0];
        if (touch.clientY < 100) {
          handleStart(touch.clientY);
        }
      },
      { passive: true }
    );

    // touchmove needs passive: false because we call preventDefault when dragging
    // Only set non-passive when we're actually in drag mode
    const touchMoveHandler = (e: TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };
    drawer.addEventListener("touchmove", touchMoveHandler, { passive: false });

    // touchend can be passive
    drawer.addEventListener("touchend", handleEnd, { passive: true });

    // Mouse events for desktop
    let mouseDown = false;
    drawer.addEventListener("mousedown", (e) => {
      if (e.clientY < 100) {
        mouseDown = true;
        handleStart(e.clientY);
      }
    });

    document.addEventListener("mousemove", (e) => {
      if (mouseDown && isDragging) {
        handleMove(e.clientY);
      }
    });

    document.addEventListener("mouseup", () => {
      if (mouseDown) {
        mouseDown = false;
        handleEnd();
      }
    });
  }
}
